import React from "react";
import { cn } from "../../lib/utils";
import type {
  UserSettingsPageProps,
  UserSettingsPageAction,
  UserSettingsPageNotice,
} from "../../contracts/user-settings";
import {
  VerticalStack,
  HorizontalStack,
  SectionEyebrow,
  DescriptionText,
  Callout,
  WorksurfaceHeader,
  RegistryStatus
} from "../blocks";

export function UserSettingsPage({
  title,
  description,
  eyebrow = "User settings",
  actions = [],
  notices = [],
  sections,
  activeSectionId,
  onSectionSelect,
  onAction,
  className,
}: UserSettingsPageProps) {
  const activeSection = sections.find(s => s.id === activeSectionId) || sections[0];
  const primaryEyebrow = eyebrow ?? "User settings";
  const pageDescription = description ?? undefined;
  const hasMultipleSections = sections.length > 1;

  const renderAction = (action: UserSettingsPageAction) => (
    <button
      key={action.id}
      type="button"
      onClick={() => onAction?.(action.id)}
      disabled={action.disabled}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded border px-3 text-xs font-bold uppercase tracking-tight transition-all shrink-0",
        action.tone === "primary"
          ? "border-primary/20 bg-primary text-primary-foreground hover:bg-primary/90"
          : action.tone === "danger"
          ? "border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20"
          : "border-border/60 bg-background text-foreground hover:bg-muted/30",
        action.disabled && "cursor-not-allowed opacity-50"
      )}
    >
      {action.icon}
      {action.label}
    </button>
  );

  const renderNotice = (notice: UserSettingsPageNotice) => {
     const variantMap: Record<string, string> = {
        success: 'success',
        warning: 'warning',
        danger: 'error',
        info: 'info',
        neutral: 'info'
     };

     return (
        <Callout
          key={notice.id}
          variant={variantMap[notice.tone || 'neutral'] as 'success' | 'warning' | 'error' | 'info'}
        >
          {notice.title && <div className="font-bold mb-0.5">{notice.title}</div>}
          {notice.description}
        </Callout>
     );
  };

  return (
    <div className={cn("flex flex-col h-full bg-background overflow-hidden", className)}>
      {/* Page Header */}
      <header className="px-4 py-4 md:px-6 md:py-6 border-b border-border/40 bg-muted/5 shrink-0 z-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-3">
          <WorksurfaceHeader
             parentLabel={primaryEyebrow}
             activeLabel={title}
             description={pageDescription}
          />
          {/* Desktop-only action buttons — mobile uses the sticky bottom bar */}
          {actions.length > 0 && (
            <HorizontalStack gap={2} className="hidden md:flex shrink-0">
              {actions.map(renderAction)}
            </HorizontalStack>
          )}
        </div>
      </header>

      {/* Mobile horizontal section tabs — only rendered when multiple sections exist */}
      {hasMultipleSections && (
        <div className="md:hidden overflow-x-auto flex gap-1 px-4 py-2 border-b border-border/40 bg-muted/5 shrink-0">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => onSectionSelect?.(section.id)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all shrink-0",
                activeSectionId === section.id
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              {section.label}
              {section.statusLabel && (
                <RegistryStatus
                  label={section.statusLabel}
                  variant={activeSectionId === section.id ? 'default' : 'muted'}
                  className="text-[10px]"
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="flex flex-col md:flex-row max-w-7xl mx-auto w-full min-h-full">
          {/* Desktop section navigation — hidden when only one section */}
          {hasMultipleSections && (
            <aside className="hidden md:flex w-64 shrink-0 px-6 py-8 border-r border-border/40 md:sticky md:top-0 h-fit flex-col">
              <VerticalStack gap={4}>
                <SectionEyebrow className="px-3">Settings</SectionEyebrow>
                <nav className="flex flex-col gap-1">
                  {sections.map(section => (
                    <button
                      key={section.id}
                      onClick={() => onSectionSelect?.(section.id)}
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all text-left group",
                        activeSectionId === section.id
                          ? "bg-primary/10 text-primary font-bold ring-1 ring-primary/20"
                          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground font-medium"
                      )}
                    >
                      <span>{section.label}</span>
                      {section.statusLabel && (
                        <RegistryStatus
                          label={section.statusLabel}
                          variant={activeSectionId === section.id ? 'default' : 'muted'}
                          className="text-[10px]"
                        />
                      )}
                    </button>
                  ))}
                </nav>
              </VerticalStack>
            </aside>
          )}

          {/* Main Content Area */}
          <main className={cn(
            "flex-1 px-4 py-6 md:px-6 md:py-8 lg:px-12",
          )}>
            <div className="max-w-4xl space-y-8">
              {/* Notices */}
              {notices.length > 0 && (
                <VerticalStack gap={3}>
                  {notices.map(renderNotice)}
                </VerticalStack>
              )}

              {/* Active Section Content */}
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="mb-8">
                   <div className="space-y-1">
                      {activeSection.eyebrow && (
                        <SectionEyebrow>{activeSection.eyebrow}</SectionEyebrow>
                      )}
                      <h2 className="text-xl font-bold text-foreground tracking-tight">
                        {activeSection.title}
                      </h2>
                      {activeSection.description && (
                        <DescriptionText className="text-sm max-w-2xl">
                          {activeSection.description}
                        </DescriptionText>
                      )}
                   </div>
                </div>

                <div className="relative">
                  {activeSection.content}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile sticky bottom action bar */}
      {actions.length > 0 && (
        <div className="md:hidden shrink-0 border-t border-border/40 bg-background px-4 py-3 flex items-center justify-end gap-2">
          {actions.map(renderAction)}
        </div>
      )}
    </div>
  );
}
